import React, { useState } from 'react';
import { EmeraldModal } from '@emerald-react/modal';
import { EmeraldButton } from '@emerald-react/button';
import { EllisAiIcon } from '@emerald-react/ellis-ai-icon';
import {
  PlusIcon,
  SearchIcon,
  StarIcon,
  BarChartIcon,
  AgentDownloadIcon,
  TrashIcon,
} from '../icons';
import type { AgentType } from '../types/chat';

export interface Agent {
  id: AgentType;
  name: string;
  description: string;
  avatar: string;
  avatarBg: string;
  avatarImage?: string;
}

interface ChatHistoryItem {
  id: string;
  title: string;
  timestamp: number;
  agent: AgentType;
}

interface LeftSidebarProps {
  agents: Agent[];
  currentAgent: AgentType;
  chatHistory: ChatHistoryItem[];
  currentChatId: string | null;
  onNewChat: () => void;
  onSelectAgent: (agentId: AgentType) => void;
  onLoadChatHistory: (chatId: string) => void;
  onDeleteChat?: (chatId: string) => void;
  isDarkMode?: boolean;
}

// ChatNavigation Component
interface ChatNavigationProps {
  onNewChat: () => void;
}

const ChatNavigation: React.FC<ChatNavigationProps> = ({ onNewChat }) => {
  return (
    <nav className="sidebar-nav">
      <button className="nav-item" onClick={onNewChat} title="New Chat">
        <PlusIcon width={20} height={20} className="icon-primary" />
        <span>New Chat</span>
      </button>
      <button className="nav-item" title="Search">
        <SearchIcon width={20} height={20} className="icon-primary" />
        <span>Search</span>
      </button>
      <button className="nav-item" title="Starred">
        <StarIcon width={20} height={20} className="icon-primary" />
        <span>Starred</span>
      </button>
      <button className="nav-item" title="Library">
        <BarChartIcon width={20} height={20} className="icon-primary" />
        <span>Library</span>
      </button>
    </nav>
  );
};

// ChatAgents Component
interface ChatAgentsProps {
  agents: Agent[];
  currentAgent: AgentType;
  onSelectAgent: (agentId: AgentType) => void;
}

const ChatAgents: React.FC<ChatAgentsProps> = ({
  agents,
  currentAgent,
  onSelectAgent,
}) => {
  return (
    <div className="sidebar-section">
      <h3 className="sidebar-section-title">Agents</h3>
      <div className="agents-list">
        {agents.map(agent => (
          <button
            key={agent.id}
            className={`agent-item ${
              currentAgent === agent.id ? 'active' : ''
            }`}
            onClick={() => onSelectAgent(agent.id)}
            title={agent.description || agent.name}
          >
            <div
              className="agent-item-avatar"
              style={{
                backgroundColor: agent.avatarImage
                  ? 'transparent'
                  : agent.avatarBg,
              }}
            >
              {agent.avatarImage ? (
                <img
                  src={agent.avatarImage}
                  alt={agent.name}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: 'inherit',
                  }}
                />
              ) : (
                agent.avatar
              )}
            </div>
            <div className="agent-item-name">{agent.name}</div>
            <AgentDownloadIcon
              width={16}
              height={16}
              className="agent-item-icon"
            />
          </button>
        ))}
      </div>
      {/* <button
        className="add-agent-btn"
        title="Add new agent"
        aria-label="Add new agent"
      >
        <PlusIcon width={16} height={16} fill="currentColor" />
        <span>Add Agent</span>
      </button> */}
    </div>
  );
};

// ChatHistory Component
interface ChatHistoryProps {
  chatHistory: ChatHistoryItem[];
  currentChatId: string | null;
  onLoadChatHistory: (chatId: string) => void;
  onDeleteChat?: (chatId: string) => void;
}

const ChatHistory: React.FC<ChatHistoryProps> = ({
  chatHistory,
  currentChatId,
  onLoadChatHistory,
  onDeleteChat,
}) => {
  const [chatIdToDelete, setChatIdToDelete] = useState<string | null>(null);
  const [chatTitleToDelete, setChatTitleToDelete] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleDeleteClick = (chatId: string, chatTitle: string) => {
    setChatIdToDelete(chatId);
    setChatTitleToDelete(chatTitle);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    if (chatIdToDelete && onDeleteChat) {
      onDeleteChat(chatIdToDelete);
    }
    setShowDeleteModal(false);
    setChatIdToDelete(null);
    setChatTitleToDelete('');
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setChatIdToDelete(null);
    setChatTitleToDelete('');
  };

  if (chatHistory.length === 0) {
    return null;
  }

  return (
    <div className="sidebar-section">
      <h3 className="sidebar-section-title">Chats</h3>
      <div className="history-list">
        {chatHistory.map(chat => (
          <div
            key={chat.id}
            className={`history-item-wrapper ${
              currentChatId === chat.id ? 'active' : ''
            }`}
          >
            <button
              className="history-item"
              onClick={() => onLoadChatHistory(chat.id)}
              title={chat.title}
            >
              {chat.title}
            </button>
            {onDeleteChat && (
              <button
                className="history-item-delete"
                onClick={e => {
                  e.stopPropagation();
                  handleDeleteClick(chat.id, chat.title);
                }}
                title="Delete chat"
                aria-label="Delete chat"
              >
                <TrashIcon width={16} height={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <EmeraldModal
        opened={showDeleteModal}
        headerText="Delete Chat"
        onClose={handleCancelDelete}
        actionTemplate={
          <>
            <div className="mdc-dialog__button">
              <EmeraldButton
                label="Cancel"
                onClick={handleCancelDelete}
                type="Secondary"
              />
            </div>
            <div className="mdc-dialog__button">
              <EmeraldButton label="Delete" onClick={handleConfirmDelete} />
            </div>
          </>
        }
      >
        <div style={{ padding: '16px 0' }}>
          Are you sure you want to delete "<strong>{chatTitleToDelete}</strong>
          "? This action cannot be undone.
        </div>
      </EmeraldModal>
    </div>
  );
};

// Main LeftSidebar Component
export const LeftSidebar = ({
  agents,
  currentAgent,
  chatHistory,
  currentChatId,
  onNewChat,
  onSelectAgent,
  onLoadChatHistory,
  onDeleteChat,
  isDarkMode,
}: LeftSidebarProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  const handleSelectAgent = (agentId: AgentType) => {
    onSelectAgent(agentId);
    setIsOpen(false); // Close sidebar on mobile after selection
  };

  const handleLoadChatHistory = (chatId: string) => {
    onLoadChatHistory(chatId);
    setIsOpen(false); // Close sidebar on mobile after selection
  };

  const handleNewChat = () => {
    onNewChat();
    setIsOpen(false); // Close sidebar on mobile after new chat
  };

  // Update global CSS variable based on sidebar state
  React.useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(
      '--emerald-collapsed-width',
      isOpen ? '3.5rem' : '0',
    );
  }, [isOpen]);

  // Manage global sidebar state via data attribute
  React.useEffect(() => {
    if (isOpen) {
      document.documentElement.setAttribute('data-ellis-sidebar-open', 'true');
    } else {
      document.documentElement.removeAttribute('data-ellis-sidebar-open');
    }
  }, [isOpen]);

  // Close sidebar on outside click
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const emeraldNav = document.querySelector('.emerald-side-navigation');
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        !emeraldNav?.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('.mobile-sidebar-toggle')
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  return (
    <>
      <div
        className={`sidebar-container ${isOpen ? 'open' : ''}`}
        ref={sidebarRef}
      >
        {/* Sidebar Header */}
        <div className="sidebar-header-wrapper">
          <div>
            <EllisAiIcon
              color={isDarkMode ? 'white' : 'default'}
              size="small"
            />
          </div>
          <span className="sidebar-header-text">Ellis AI D&T Chat</span>
        </div>

        {/* Sidebar Content */}
        <div className="sidebar-content">
          <ChatNavigation onNewChat={handleNewChat} />
          <ChatAgents
            agents={agents}
            currentAgent={currentAgent}
            onSelectAgent={handleSelectAgent}
          />
          <ChatHistory
            chatHistory={chatHistory}
            currentChatId={currentChatId}
            onLoadChatHistory={handleLoadChatHistory}
            onDeleteChat={onDeleteChat}
          />
        </div>
      </div>

      {/* Mobile toggle button */}
      <button
        className="mobile-sidebar-toggle"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle sidebar"
      >
        ☰
      </button>
    </>
  );
};
